if (!customElements.get('product-bundle-builder')) {
  class ProductBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.products = [...this.querySelectorAll('[data-bundle-product]')];
      this.steps = [...this.querySelectorAll('[data-bundle-step]')];
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.progress = this.querySelector('[data-bundle-progress]');
      this.total = this.querySelector('[data-bundle-total]');
      this.originalTotal = this.querySelector('[data-bundle-original-total]');
      this.error = this.querySelector('[data-bundle-error]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.maxQuantityPerProduct = Math.max(Number(this.dataset.maxQuantityPerProduct) || 2, 1);

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.submitButton?.addEventListener('click', this.addBundleToCart.bind(this));
      this.update();
    }

    handleClick(event) {
      const button = event.target.closest('[data-bundle-action]');
      if (!button || button.disabled) return;

      const product = button.closest('[data-bundle-product]');
      const currentQuantity = Number(product.dataset.quantity) || 0;

      if (button.dataset.bundleAction === 'plus' && currentQuantity < this.maxQuantityPerProduct) {
        product.dataset.quantity = currentQuantity + 1;
      }

      if (button.dataset.bundleAction === 'minus' && currentQuantity > 0) {
        product.dataset.quantity = currentQuantity - 1;
      }

      this.update();
    }

    handleChange(event) {
      if (!event.target.matches('[data-bundle-variant]')) return;

      const product = event.target.closest('[data-bundle-product]');
      const price = this.getVariantPrice(event.target);
      const priceElement = product.querySelector('[data-bundle-product-price]');
      if (priceElement) priceElement.textContent = this.formatMoney(price);
      this.update();
    }

    getVariant(product) {
      return product.querySelector('[data-bundle-variant]');
    }

    getVariantPrice(variantElement) {
      if (variantElement.tagName === 'SELECT') {
        return Number(variantElement.selectedOptions[0]?.dataset.price) || 0;
      }

      return Number(variantElement.dataset.price) || 0;
    }

    isVariantAvailable(variantElement) {
      if (variantElement.tagName === 'SELECT') {
        return variantElement.selectedOptions[0]?.dataset.available === 'true';
      }

      return variantElement.dataset.available === 'true';
    }

    getTotalQuantity() {
      return this.products.reduce((total, product) => total + (Number(product.dataset.quantity) || 0), 0);
    }

    getActiveStep(quantity) {
      return this.steps.reduce((activeStep, step) => {
        const threshold = Number(step.dataset.threshold) || 0;
        return quantity >= threshold && (!activeStep || threshold >= Number(activeStep.dataset.threshold))
          ? step
          : activeStep;
      }, null);
    }

    update() {
      const totalQuantity = this.getTotalQuantity();
      const activeStep = this.getActiveStep(totalQuantity);
      const activeIndex = activeStep ? this.steps.indexOf(activeStep) : -1;
      let subtotal = 0;

      this.products.forEach((product) => {
        const quantity = Number(product.dataset.quantity) || 0;
        const variant = this.getVariant(product);
        subtotal += quantity * this.getVariantPrice(variant);

        const quantityElement = product.querySelector('[data-bundle-quantity]');
        const plusButton = product.querySelector('[data-bundle-action="plus"]');
        if (quantityElement) quantityElement.textContent = quantity;
        if (plusButton) {
          plusButton.disabled = quantity >= this.maxQuantityPerProduct || !this.isVariantAvailable(variant);
        }
      });

      this.steps.forEach((step, index) => {
        step.classList.toggle('is-active', totalQuantity >= Number(step.dataset.threshold));
        step.classList.toggle('is-current', index === activeIndex);
      });

      const progressPercentage = activeIndex < 0 ? 0 : (activeIndex / Math.max(this.steps.length - 1, 1)) * 100;
      if (this.progress) this.progress.style.width = `${progressPercentage}%`;

      const discount = activeStep ? Number(activeStep.dataset.discount) || 0 : 0;
      const discountedTotal = Math.round(subtotal * (1 - discount / 100));
      if (this.total) this.total.textContent = this.formatMoney(discountedTotal);
      if (this.originalTotal) {
        this.originalTotal.textContent = this.formatMoney(subtotal);
        this.originalTotal.hidden = discount === 0 || subtotal === 0;
      }

      if (this.submitButton && !this.submitButton.classList.contains('btn--loading')) {
        this.submitButton.disabled = totalQuantity === 0;
      }
    }

    formatMoney(cents) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }

    buildItems() {
      const quantitiesByVariant = new Map();
      const activeStep = this.getActiveStep(this.getTotalQuantity());
      const tier = activeStep?.dataset.threshold || '0';

      this.products.forEach((product) => {
        const quantity = Number(product.dataset.quantity) || 0;
        if (quantity === 0) return;

        const variantId = this.getVariant(product).value;
        quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + quantity);
      });

      return [...quantitiesByVariant].map(([id, quantity]) => ({
        id,
        quantity,
        properties: {
          _bundle_offer: this.dataset.bundleId,
          _bundle_tier: tier,
        },
      }));
    }

    async addBundleToCart(event) {
      event.preventDefault();
      const items = this.buildItems();
      if (!items.length) return;

      if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType !== 'drawer') {
        FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { items } });
        return;
      }

      this.showError();
      this.toggleLoading(true);

      try {
        const localeRoot = window.Shopify?.routes?.root || '/';
        const cartAddUrl = `${localeRoot}cart/add.js`;
        const cartUrl = `${localeRoot}cart.js`;
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const state = await this.parseJsonResponse(response);

        if (!response.ok || state.status) {
          throw new Error(state.description || 'Le bundle n’a pas pu être ajouté au panier.');
        }

        const cartResponse = await fetch(cartUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const cart = await this.parseJsonResponse(cartResponse);
        cart.sections = state.sections;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
        this.cartDrawer?.show(this.submitButton);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.toggleLoading(false);
        this.update();
      }
    }

    async parseJsonResponse(response) {
      const responseText = await response.text();

      try {
        return JSON.parse(responseText);
      } catch (error) {
        throw new Error('Shopify a renvoyé une réponse inattendue. Rechargez la page puis réessayez.');
      }
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message;
    }

    toggleLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('btn--loading', isLoading);
      this.submitButton.disabled = isLoading;
    }
  }

  customElements.define('product-bundle-builder', ProductBundleBuilder);
}
